import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../environments/environment';

/** Returned by {@link CryptoService.generateSecureKeyAndIV}. */
export interface SecureKeyBundle {
  /** Plain AES key (32-char hex) — use to encrypt/decrypt data. */
  secureKey: string;
  /** Plain AES IV (16-char hex) — use to encrypt/decrypt data. */
  secureIV: string;
  /** `secureKey` AES-encrypted with `environment.CRYPTOJSKEY` — send to server. */
  encsecureKey: string;
  /** `secureIV` AES-encrypted with `environment.CRYPTOJSKEY` — send to server. */
  encsecureIV: string;
}

/**
 * Handles AES encrypt/decrypt operations used for e-signature data.
 *
 * @remarks
 * The master passphrase (`environment.CRYPTOJSKEY`) wraps the per-document
 * AES key and IV stored on the server.  Both the key and IV are generated
 * with a cryptographically secure random source on every call so each
 * document has unique encryption material.
 *
 * Call flow:
 *
 * **Decrypt (display):**
 * 1. Decrypt `esign_securekey` / `esign_secureiv` with `CRYPTOJSKEY`
 * 2. Use the unwrapped key+IV to decrypt `esign_data` → base64 PNG
 *
 * **Encrypt (submit):**
 * 1. `generateSecureKeyAndIV()` — generate random key+IV, wrap with `CRYPTOJSKEY`
 * 2. `encryptRawData(secureKey, secureIV, data)` — encrypt the affidavit document
 * 3. Send `encsecureKey`, `encsecureIV`, and encrypted document to the server
 */
@Injectable({ providedIn: 'root' })
export class CryptoService {

  /**
   * Returns the master passphrase from the environment, throwing if it has
   * not been injected at build time.
   *
   * @throws {Error} When `CRYPTOJSKEY` is empty — signals a misconfigured build.
   */
  private masterKey(): string {
    const key = environment.CRYPTOJSKEY;
    if (!key) {
      throw new Error(
        '[CryptoService] CRYPTOJSKEY is not set. ' +
        'Inject it at build time: ng build --define "environment.CRYPTOJSKEY=\\"$CRYPTO_KEY\\""',
      );
    }
    return key;
  }

  /**
   * Decrypts `esign_data` from the GET /esignature response.
   *
   * @param encryptedKey  - `esign_securekey` from the API (AES-encrypted with master key).
   * @param encryptedIV   - `esign_secureiv` from the API (AES-encrypted with master key).
   * @param encryptedData - `esign_data` from the API (AES-encrypted with the unwrapped key).
   * @returns Decrypted UTF-8 string (typically a base64-encoded PNG).
   * @throws {Error} When `CRYPTOJSKEY` is not configured.
   */
  decryptRawData(encryptedKey: string, encryptedIV: string, encryptedData: string): string {
    const masterKey   = this.masterKey();
    const dcsecurekey = CryptoJS.AES.decrypt(encryptedKey, masterKey).toString(CryptoJS.enc.Utf8);
    const dcsecureiv  = CryptoJS.AES.decrypt(encryptedIV,  masterKey).toString(CryptoJS.enc.Utf8);
    const decrypted   = CryptoJS.AES.decrypt(encryptedData, dcsecurekey, {
      iv:      CryptoJS.enc.Utf8.parse(dcsecureiv),
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  /**
   * AES-encrypts `rawData` with the provided plain key and IV.
   *
   * @param secureKey - Plain 32-char hex key from {@link generateSecureKeyAndIV}.
   * @param secureIV  - Plain 16-char hex IV from {@link generateSecureKeyAndIV}.
   * @param rawData   - Plaintext to encrypt (e.g. signed affidavit HTML).
   * @returns CryptoJS cipher-text string (Base64).
   */
  encryptRawData(secureKey: string, secureIV: string, rawData: string): string {
    return CryptoJS.AES.encrypt(rawData, secureKey, {
      iv:      CryptoJS.enc.Utf8.parse(secureIV),
      padding: CryptoJS.pad.Pkcs7,
    }).toString();
  }

  /**
   * Generates a cryptographically random AES key and IV, then wraps them
   * with the master passphrase so they can be stored server-side.
   *
   * @remarks
   * Both the key (128-bit → 32 hex chars) and IV (64-bit → 16 hex chars) are
   * produced by `CryptoJS.lib.WordArray.random()` which uses the platform's
   * CSPRNG.  Each call returns unique material — no two documents share the
   * same key or IV.
   *
   * @returns `{ secureKey, secureIV, encsecureKey, encsecureIV }` — use the
   *   plain variants for local encryption and the `enc*` variants for the API payload.
   */
  generateSecureKeyAndIV(): SecureKeyBundle {
    const masterKey    = this.masterKey();
    const secureKey    = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);
    const secureIV     = CryptoJS.lib.WordArray.random(8).toString(CryptoJS.enc.Hex);
    const encsecureKey = CryptoJS.AES.encrypt(secureKey, masterKey).toString();
    const encsecureIV  = CryptoJS.AES.encrypt(secureIV,  masterKey).toString();
    return { secureKey, secureIV, encsecureKey, encsecureIV };
  }
}
